import { loadTokens } from "./auth-client";

export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    details?: unknown;
  };
}

const BASE_URL =
  (import.meta.env.VITE_API_BASE_URL &&
    import.meta.env.VITE_API_BASE_URL.replace(/\/$/, "")) ||
  (import.meta.env.DEV
    ? "http://localhost:8580"
    : "https://api.pahfacturar.com");

export class ApiError extends Error {
  status: number;
  code: string;
  details?: unknown;

  constructor(status: number, body: ApiErrorBody | { message?: string }) {
    const code = "error" in body ? body.error.code : "UNKNOWN";
    const message =
      "error" in body ? body.error.message : body.message ?? "Request failed";
    super(message);
    this.status = status;
    this.code = code;
    this.details = "error" in body ? body.error.details : undefined;
  }
}

let onUnauthorized: (() => void) | null = null;
let unauthorizedHandled = false;

export function setOnUnauthorized(handler: (() => void) | null) {
  onUnauthorized = handler;
  if (!handler) unauthorizedHandled = false;
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  query?: Record<string, unknown>;
  signal?: AbortSignal;
}

function buildUrl(path: string, query?: Record<string, unknown>): string {
  let url: URL;
  if (path.startsWith("http://") || path.startsWith("https://")) {
    url = new URL(path);
  } else {
    url = new URL(
      path.startsWith("/") ? path : `/${path}`,
      window.location.origin,
    );
    if (BASE_URL) {
      const base = new URL(BASE_URL, window.location.origin);
      url.protocol = base.protocol;
      url.host = base.host;
    }
  }
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null || value === "") continue;
      url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

export async function apiRequest<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { method = "GET", body, query, signal } = options;

  const headers: Record<string, string> = {};
  const tokens = loadTokens();
  if (tokens?.access) {
    headers.Authorization = `Bearer ${tokens.access}`;
  }

  let fetchBody: BodyInit | undefined;
  if (body === undefined) {
    fetchBody = undefined;
  } else {
    headers["Content-Type"] = "application/json";
    fetchBody = JSON.stringify(body);
  }

  const res = await fetch(buildUrl(path, query), {
    method,
    headers,
    body: fetchBody,
    signal,
  });

  if (res.status === 401 && onUnauthorized && !unauthorizedHandled) {
    unauthorizedHandled = true;
    onUnauthorized();
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const payload = text ? (JSON.parse(text) as unknown) : ({} as unknown);

  if (!res.ok) {
    throw new ApiError(res.status, payload as ApiErrorBody);
  }
  return payload as T;
}

export interface ApiBinaryResponse {
  blob: Blob;
  fileName: string | null;
}

function fileNameFromContentDisposition(value: string | null): string | null {
  if (!value) return null;
  const encoded = value.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded);
    } catch {
      // Fall back to the ASCII filename below.
    }
  }
  return value.match(/filename="([^"]*)"/i)?.[1] ?? null;
}

/** Authenticated binary request for APIs that stream files instead of JSON. */
export async function apiRequestBinary(
  path: string,
  options: Pick<RequestOptions, "query" | "signal"> = {},
): Promise<ApiBinaryResponse> {
  const headers: Record<string, string> = {};
  const tokens = loadTokens();
  if (tokens?.access) {
    headers.Authorization = `Bearer ${tokens.access}`;
  }

  const res = await fetch(buildUrl(path, options.query), {
    method: "GET",
    headers,
    signal: options.signal,
  });

  if (res.status === 401 && onUnauthorized && !unauthorizedHandled) {
    unauthorizedHandled = true;
    onUnauthorized();
  }

  if (!res.ok) {
    const text = await res.text();
    let payload: ApiErrorBody | { message?: string } = {
      message: text || "Request failed",
    };
    if (text) {
      try {
        payload = JSON.parse(text) as ApiErrorBody;
      } catch {
        // Preserve the server text in the generic error shape.
      }
    }
    throw new ApiError(res.status, payload);
  }

  return {
    blob: await res.blob(),
    fileName: fileNameFromContentDisposition(res.headers.get("content-disposition")),
  };
}
